import { CircleAlert, Paperclip, X } from 'lucide-react';
import {
  useId,
  useState,
  type DragEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { classes } from '../outils/classes';
import styles from './Champ.module.css';

interface ProprietesCommunes {
  libelle: ReactNode;
  aide?: ReactNode;
  erreur?: string | null;
  /** Affiche « facultatif » à côté du libellé (les champs obligatoires portent un astérisque). */
  facultatif?: boolean;
  classeGroupe?: string;
}

function useIdentifiants(id?: string) {
  const genere = useId();
  const base = id ?? `champ-${genere}`;
  return { id: base, aide: `${base}-aide`, erreur: `${base}-erreur` };
}

function Enveloppe({
  ids,
  libelle,
  aide,
  erreur,
  obligatoire,
  facultatif,
  classeGroupe,
  children,
}: ProprietesCommunes & { ids: ReturnType<typeof useIdentifiants>; obligatoire?: boolean; children: ReactNode }) {
  return (
    <div className={classes(styles.groupe, classeGroupe)}>
      <label className={styles.libelle} htmlFor={ids.id}>
        {libelle}
        {obligatoire ? (
          <span className={styles.obligatoire} aria-hidden="true">
            *
          </span>
        ) : null}
        {facultatif ? <span className={styles.facultatif}>(facultatif)</span> : null}
      </label>
      {children}
      {aide ? (
        <span id={ids.aide} className={styles.aide}>
          {aide}
        </span>
      ) : null}
      {erreur ? (
        <span id={ids.erreur} className={styles.erreur}>
          <CircleAlert aria-hidden strokeWidth={1.75} />
          {erreur}
        </span>
      ) : null}
    </div>
  );
}

function decritPar(ids: ReturnType<typeof useIdentifiants>, aide: unknown, erreur: unknown, existant?: string) {
  return [existant, aide ? ids.aide : null, erreur ? ids.erreur : null].filter(Boolean).join(' ') || undefined;
}

export interface ProprietesChamp extends ProprietesCommunes, Omit<InputHTMLAttributes<HTMLInputElement>, 'children'> {
  ref?: Ref<HTMLInputElement>;
}

/** Champ de saisie : libellé au-dessus, aide en dessous, erreur explicite avec icône. */
export function Champ({ libelle, aide, erreur, facultatif, classeGroupe, id, className, required, ...reste }: ProprietesChamp) {
  const ids = useIdentifiants(id);
  return (
    <Enveloppe ids={ids} libelle={libelle} aide={aide} erreur={erreur} obligatoire={required} facultatif={facultatif} classeGroupe={classeGroupe}>
      <input
        id={ids.id}
        className={classes(styles.controle, className)}
        aria-invalid={erreur ? true : undefined}
        aria-describedby={decritPar(ids, aide, erreur, reste['aria-describedby'])}
        required={required}
        {...reste}
      />
    </Enveloppe>
  );
}

export interface ProprietesZoneTexte extends ProprietesCommunes, Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'children'> {
  ref?: Ref<HTMLTextAreaElement>;
}

export function ZoneTexte({ libelle, aide, erreur, facultatif, classeGroupe, id, className, required, ...reste }: ProprietesZoneTexte) {
  const ids = useIdentifiants(id);
  return (
    <Enveloppe ids={ids} libelle={libelle} aide={aide} erreur={erreur} obligatoire={required} facultatif={facultatif} classeGroupe={classeGroupe}>
      <textarea
        id={ids.id}
        className={classes(styles.controle, className)}
        aria-invalid={erreur ? true : undefined}
        aria-describedby={decritPar(ids, aide, erreur)}
        required={required}
        {...reste}
      />
    </Enveloppe>
  );
}

export interface OptionSelection {
  valeur: string;
  libelle: string;
  desactivee?: boolean;
}

export interface ProprietesSelection extends ProprietesCommunes, Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  options: OptionSelection[];
  /** Première option vide (par exemple « Choisir un service »). */
  optionVide?: string;
  ref?: Ref<HTMLSelectElement>;
}

export function Selection({ libelle, aide, erreur, facultatif, classeGroupe, id, className, options, optionVide, required, ...reste }: ProprietesSelection) {
  const ids = useIdentifiants(id);
  return (
    <Enveloppe ids={ids} libelle={libelle} aide={aide} erreur={erreur} obligatoire={required} facultatif={facultatif} classeGroupe={classeGroupe}>
      <select
        id={ids.id}
        className={classes(styles.controle, className)}
        aria-invalid={erreur ? true : undefined}
        aria-describedby={decritPar(ids, aide, erreur)}
        required={required}
        {...reste}
      >
        {optionVide !== undefined ? <option value="">{optionVide}</option> : null}
        {options.map((o) => (
          <option key={o.valeur} value={o.valeur} disabled={o.desactivee}>
            {o.libelle}
          </option>
        ))}
      </select>
    </Enveloppe>
  );
}

export interface ProprietesDateChamp extends Omit<ProprietesChamp, 'type'> {
  /** Date et heure (datetime-local) plutôt que date seule. */
  avecHeure?: boolean;
}

export function DateChamp({ avecHeure, ...reste }: ProprietesDateChamp) {
  return <Champ type={avecHeure ? 'datetime-local' : 'date'} {...reste} />;
}

export interface ProprietesCaseACocher extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'children'> {
  libelle: ReactNode;
  ref?: Ref<HTMLInputElement>;
}

export function CaseACocher({ libelle, className, id, ...reste }: ProprietesCaseACocher) {
  const genere = useId();
  const ident = id ?? `case-${genere}`;
  return (
    <label className={classes(styles.case, className)} htmlFor={ident}>
      <input id={ident} type="checkbox" {...reste} />
      <span>{libelle}</span>
    </label>
  );
}

export function tailleLisible(octets: number): string {
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${(octets / 1024).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Ko`;
  return `${(octets / (1024 * 1024)).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mo`;
}

export interface ProprietesFichierChamp extends ProprietesCommunes {
  id?: string;
  fichiers: File[];
  onFichiers: (fichiers: File[]) => void;
  multiple?: boolean;
  /** Types acceptés, par exemple « .pdf,.jpg,.png ». */
  accept?: string;
  /** Taille maximale par fichier en octets ; un fichier plus gros est refusé avec un message. */
  tailleMax?: number;
  required?: boolean;
  disabled?: boolean;
}

/** Dépôt de fichiers par sélection ou glisser-déposer (numérisation de courriers, pièces). */
export function FichierChamp({
  libelle,
  aide,
  erreur,
  facultatif,
  classeGroupe,
  id,
  fichiers,
  onFichiers,
  multiple = true,
  accept,
  tailleMax,
  required,
  disabled,
}: ProprietesFichierChamp) {
  const ids = useIdentifiants(id);
  const [survol, setSurvol] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);

  const ajouter = (liste: FileList | null) => {
    if (!liste) return;
    const nouveaux = [...liste];
    const trop = tailleMax ? nouveaux.filter((f) => f.size > tailleMax) : [];
    setRefus(
      trop.length > 0 && tailleMax
        ? `Fichier refusé, taille supérieure à ${tailleLisible(tailleMax)} : ${trop.map((f) => f.name).join(', ')}`
        : null,
    );
    const acceptes = nouveaux.filter((f) => !trop.includes(f));
    onFichiers(multiple ? [...fichiers, ...acceptes] : acceptes.slice(0, 1));
  };

  const deposer = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setSurvol(false);
    if (!disabled) ajouter(e.dataTransfer.files);
  };

  const erreurAffichee = erreur ?? refus;
  return (
    <div className={classes(styles.groupe, classeGroupe)}>
      <span className={styles.libelle} id={`${ids.id}-libelle`}>
        {libelle}
        {required ? (
          <span className={styles.obligatoire} aria-hidden="true">
            *
          </span>
        ) : null}
        {facultatif ? <span className={styles.facultatif}>(facultatif)</span> : null}
      </span>
      <div
        className={styles.zoneDepot}
        data-survol={survol}
        onDragOver={(e) => {
          e.preventDefault();
          setSurvol(true);
        }}
        onDragLeave={() => setSurvol(false)}
        onDrop={deposer}
      >
        <input
          id={ids.id}
          type="file"
          className={styles.fichierCache}
          multiple={multiple}
          accept={accept}
          disabled={disabled}
          aria-labelledby={`${ids.id}-libelle ${ids.id}-bouton`}
          aria-describedby={decritPar(ids, aide, erreurAffichee)}
          aria-invalid={erreurAffichee ? true : undefined}
          onChange={(e) => {
            ajouter(e.target.files);
            e.target.value = '';
          }}
        />
        <label htmlFor={ids.id} className={styles.libelleFichier} id={`${ids.id}-bouton`}>
          <Paperclip aria-hidden strokeWidth={1.75} width={20} height={20} />
          {multiple ? 'Choisir des fichiers' : 'Choisir un fichier'}
        </label>
        <span className={styles.aide}>ou glissez-les dans cette zone</span>
        {fichiers.length > 0 ? (
          <ul className={styles.listeFichiers} aria-label="Fichiers sélectionnés">
            {fichiers.map((f, i) => (
              <li key={`${f.name}-${i}`}>
                <span>
                  {f.name} <span className="cc-num">({tailleLisible(f.size)})</span>
                </span>
                <button
                  type="button"
                  className={styles.libelleFichier}
                  onClick={() => onFichiers(fichiers.filter((_, j) => j !== i))}
                  aria-label={`Retirer ${f.name}`}
                  disabled={disabled}
                >
                  <X aria-hidden strokeWidth={1.75} width={18} height={18} />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {aide ? (
        <span id={ids.aide} className={styles.aide}>
          {aide}
        </span>
      ) : null}
      {erreurAffichee ? (
        <span id={ids.erreur} className={styles.erreur} role="alert">
          <CircleAlert aria-hidden strokeWidth={1.75} />
          {erreurAffichee}
        </span>
      ) : null}
    </div>
  );
}
